import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Download, Globe, Mail, RefreshCw, UserRound, X } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import { localeOf } from '../../assets/js/locale';
import Loader from '../common/Loader';
import Modal from '../common/Modal';

const API = process.env.REACT_APP_API_URL;
const FILTERS = ['pendiente', 'aprobada', 'rechazada', 'todas'];

// Solicitudes de verificación de ONG: se aprueban o se rechazan aquí
const OngRequestsTab = ({ onNotice }) => {
  const { t, language } = useLanguage();
  const locale = localeOf(language);
  const [requests, setRequests] = useState([]);
  const [state, setState] = useState('loading');
  const [filter, setFilter] = useState('pendiente');
  const [savingId, setSavingId] = useState(null);
  const [toReject, setToReject] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setState('loading');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/ong-requests`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
      setState('ready');
    } catch (error) {
      console.error('Error al cargar las solicitudes de ONG:', error);
      setState('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => requests.reduce((acc, request) => {
    acc[request.estado] = (acc[request.estado] || 0) + 1;
    acc.todas += 1;
    return acc;
  }, { pendiente: 0, aprobada: 0, rechazada: 0, todas: 0 }), [requests]);

  const visible = useMemo(
    () => (filter === 'todas' ? requests : requests.filter((request) => request.estado === filter)),
    [requests, filter]
  );

  const formatDate = (value) => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
      : '';
  };

  const decide = async (request, estado, motivo = '') => {
    setSavingId(request.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/ong-requests/${request.id}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ estado, motivo })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);

      onNotice(
        'confirm',
        t(estado === 'aprobada' ? 'adm_ong_approved_title' : 'adm_ong_rejected_title'),
        t(data.emailEnviado === false ? 'adm_ong_decided_no_email' : 'adm_ong_decided_text').replace('{org}', request.organizacion)
      );
      await load();
    } catch (error) {
      console.error('Error al resolver la solicitud de ONG:', error);
      onNotice('warning', t('adm_ong_error_decide'), error.message);
    } finally {
      setSavingId(null);
    }
  };

  const downloadDocument = async (request) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/ong-requests/${request.id}/document`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = request.documento_nombre || `ong-${request.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar el documento de la ONG:', error);
      onNotice('warning', t('adm_ong_error_document'), error.message);
    }
  };

  if (state === 'loading') return <div className="tp-admin__loading"><Loader /></div>;

  return (
    <>
      <header className="tp-admin__block-head">
        <h2 className="tp-admin__block-title">{t('adm_ong_title')}</h2>
        <span className="tp-admin__block-count">{counts.pendiente}</span>
      </header>
      <p className="tp-admin__note">{t('adm_ong_intro')}</p>

      <div className="tp-dash__controls">
        <div className="tp-dash__tabs" role="group" aria-label={t('adm_ong_filter_label')}>
          {FILTERS.map((key) => (
            <button
              key={key}
              type="button"
              className={`tp-dash__tab ${filter === key ? 'is-active' : ''}`.trim()}
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
            >
              {t(`adm_ong_filter_${key}`)} ({counts[key] || 0})
            </button>
          ))}
        </div>
        <button type="button" className="btn btn--ghost btn--sm" onClick={load}>
          <RefreshCw size={15} strokeWidth={2} aria-hidden="true" />
          {t('adm_ong_refresh')}
        </button>
      </div>

      {state === 'error' && (
        <p className="tp-form__alert" role="alert">{t('adm_ong_error_load')}</p>
      )}

      {state === 'ready' && visible.length === 0 && (
        <p className="tp-dash__no-match">{t('adm_ong_empty')}</p>
      )}

      {visible.length > 0 && (
        <ul className="tp-dash__list">
          {visible.map((request) => (
            <li key={request.id} className="tp-dash__item tp-admin__row--simple">
              <div className="tp-dash__main">
                <div className="tp-dash__title-row">
                  <h3 className="tp-dash__title">{request.organizacion}</h3>
                  <span className={`tp-dash__status is-ong-${request.estado}`}>{t(`adm_ong_state_${request.estado}`)}</span>
                </div>

                <div className="tp-dash__meta">
                  <span><Mail size={14} strokeWidth={2} aria-hidden="true" />{request.email}</span>
                  <span>{t('adm_ong_id')} <strong>{request.identificador}</strong></span>
                  {request.web && (
                    <a href={request.web} target="_blank" rel="noreferrer" className="tp-admin__link">
                      <Globe size={14} strokeWidth={2} aria-hidden="true" />
                      {request.web.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                  {request.usuario_nombre && (
                    <span><UserRound size={14} strokeWidth={2} aria-hidden="true" />{request.usuario_nombre}</span>
                  )}
                  <span>{formatDate(request.created_at)}</span>
                </div>

                <p className="tp-admin__ong-mission">{request.mision}</p>

                {request.estado !== 'pendiente' && (
                  <p className="tp-admin__ong-review">
                    {t('adm_ong_reviewed')
                      .replace('{name}', request.revisor_nombre || '—')
                      .replace('{date}', formatDate(request.fecha_revision))}
                    {request.motivo ? ` · ${request.motivo}` : ''}
                  </p>
                )}
              </div>

              <div className="tp-dash__actions">
                {request.tiene_documento && (
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => downloadDocument(request)}>
                    <Download size={15} strokeWidth={2} aria-hidden="true" />
                    {t('adm_ong_document')}
                  </button>
                )}
                {request.estado === 'pendiente' && (
                  <>
                    <button
                      type="button"
                      className="btn btn--outline btn--sm"
                      onClick={() => { setToReject(request); setReason(''); }}
                      disabled={savingId === request.id}
                    >
                      <X size={15} strokeWidth={2} aria-hidden="true" />
                      {t('adm_ong_reject')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      onClick={() => decide(request, 'aprobada')}
                      disabled={savingId === request.id}
                    >
                      <Check size={15} strokeWidth={2} aria-hidden="true" />
                      {t('adm_ong_approve')}
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={Boolean(toReject)}
        onOpenChange={(open) => { if (!open) setToReject(null); }}
        variant="warning"
        title={t('adm_ong_reject_title')}
        description={toReject ? t('adm_ong_reject_text').replace('{org}', toReject.organizacion) : ''}
        cancelText={t('common_cancel')}
        confirmText={t('adm_ong_reject')}
        onCancel={() => setToReject(null)}
        onConfirm={() => {
          const request = toReject;
          setToReject(null);
          decide(request, 'rechazada', reason.trim());
        }}
        size="md"
      >
        <div className="tp-form tp-form--bare">
          <label className="tp-field" htmlFor="ong-reject-reason">
            <span>{t('adm_ong_reject_reason')}</span>
            <textarea
              id="ong-reject-reason"
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('adm_ong_reject_reason_placeholder')}
            />
          </label>
        </div>
      </Modal>
    </>
  );
};

export default OngRequestsTab;
